import { SeasonShowApi, prefillSeasonListApi, seasonUpdate } from "../../api/HttpApis";

// Seasons only store access (Free/Paid), no access type or pricing, so that's all
// we send and compare. The update endpoint only writes fields present in the
// body, so the rest of the season is left untouched.
const toSeasonAccess = (data) => (data?.access === "Paid" ? "Paid" : "Free");

export const buildSeasonAccessPayload = (seriesId, accessFields) => {
  const payload = new FormData();
  payload.append("access", toSeasonAccess(accessFields));
  payload.append("series_id", seriesId || "");
  return payload;
};

// Copies the series' access (from getAccessFields) onto every season that doesn't
// already match it, e.g. Paid when Subscription is on, or back to Free when it's
// turned off. Seasons are updated independently, so one failure doesn't block the
// rest. Resolves to { updated, failed } where failed lists the seasons' names.
export const applyAccessToSeasons = async (seriesId, accessFields) => {
  const listRes = await prefillSeasonListApi(seriesId);
  if (!listRes) throw new Error("Failed to load seasons");
  const seasons = listRes?.data?.data || [];
  if (!seasons.length) return { updated: 0, failed: [] };

  const results = await Promise.allSettled(
    seasons.map(async (season) => {
      const seasonId = season.season_id || season.id;
      const res = await SeasonShowApi(seriesId, seasonId);
      const data = res?.data?.data || res?.data;
      // A swallowed 403 comes back as an empty list; never re-save that as a season.
      if (!data || Array.isArray(data)) throw new Error(`Season ${seasonId} not found`);
      if (toSeasonAccess(data) === toSeasonAccess(accessFields)) return false;

      await seasonUpdate(seriesId, seasonId, buildSeasonAccessPayload(seriesId, accessFields));
      return true;
    })
  );

  return {
    updated: results.filter((r) => r.status === "fulfilled" && r.value).length,
    failed: seasons
      .filter((_, i) => results[i].status === "rejected")
      .map((s) => s.name || `Season ${s.season_number ?? s.season_id ?? s.id}`),
  };
};
